"use client";

import { useEffect, useRef, useState } from "react";

const MAX_MINUTES = 20;
type Phase = "idle" | "ready" | "decoding" | "rendering" | "done" | "error";
type Settings = { vocalReduction: number; width: number; punch: number; clarity: number };

function encodeWav(rendered: AudioBuffer): Blob {
  const channels = 2;
  const sampleRate = rendered.sampleRate;
  const frames = rendered.length;
  const data = new ArrayBuffer(44 + frames * channels * 2);
  const view = new DataView(data);
  const write = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index++) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };

  write(0, "RIFF");
  view.setUint32(4, 36 + frames * channels * 2, true);
  write(8, "WAVE");
  write(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channels * 2, true);
  view.setUint16(32, channels * 2, true);
  view.setUint16(34, 16, true);
  write(36, "data");
  view.setUint32(40, frames * channels * 2, true);

  const left = rendered.getChannelData(0);
  const right = rendered.numberOfChannels > 1 ? rendered.getChannelData(1) : left;
  let offset = 44;
  for (let index = 0; index < frames; index++) {
    const leftSample = Math.max(-1, Math.min(1, left[index]));
    const rightSample = Math.max(-1, Math.min(1, right[index]));
    view.setInt16(offset, leftSample < 0 ? leftSample * 0x8000 : leftSample * 0x7fff, true);
    offset += 2;
    view.setInt16(offset, rightSample < 0 ? rightSample * 0x8000 : rightSample * 0x7fff, true);
    offset += 2;
  }
  return new Blob([data], { type: "audio/wav" });
}

async function enhanceInstrumental(file: File, settings: Settings, onPhase: (phase: Phase) => void) {
  onPhase("decoding");
  const decodingContext = new AudioContext();
  const sourceBuffer = await decodingContext
    .decodeAudioData(await file.arrayBuffer())
    .finally(() => decodingContext.close());

  if (sourceBuffer.duration > MAX_MINUTES * 60) {
    throw new Error(`Choose a track under ${MAX_MINUTES} minutes.`);
  }

  onPhase("rendering");
  const context = new OfflineAudioContext(2, sourceBuffer.length, sourceBuffer.sampleRate);
  const source = context.createBufferSource();
  source.buffer = sourceBuffer;
  const splitter = context.createChannelSplitter(2);
  source.connect(splitter);

  const leftToMid = context.createGain();
  const rightToMid = context.createGain();
  leftToMid.gain.value = 0.5;
  rightToMid.gain.value = 0.5;
  const leftToSide = context.createGain();
  const rightToSide = context.createGain();
  leftToSide.gain.value = 0.5;
  rightToSide.gain.value = -0.5;
  const midBus = context.createGain();
  const sideBus = context.createGain();
  const rightChannel = sourceBuffer.numberOfChannels > 1 ? 1 : 0;

  splitter.connect(leftToMid, 0);
  splitter.connect(rightToMid, rightChannel);
  splitter.connect(leftToSide, 0);
  splitter.connect(rightToSide, rightChannel);
  leftToMid.connect(midBus);
  rightToMid.connect(midBus);
  leftToSide.connect(sideBus);
  rightToSide.connect(sideBus);

  const midLow = context.createBiquadFilter();
  midLow.type = "lowpass";
  midLow.frequency.value = 180;
  const midVocalHighpass = context.createBiquadFilter();
  midVocalHighpass.type = "highpass";
  midVocalHighpass.frequency.value = 180;
  const midVocalLowpass = context.createBiquadFilter();
  midVocalLowpass.type = "lowpass";
  midVocalLowpass.frequency.value = 5200;
  const vocalGain = context.createGain();
  vocalGain.gain.value = 1 - settings.vocalReduction * 0.82;
  const midAir = context.createBiquadFilter();
  midAir.type = "highpass";
  midAir.frequency.value = 5200;
  const midRecombine = context.createGain();

  midBus.connect(midLow).connect(midRecombine);
  midBus.connect(midVocalHighpass).connect(midVocalLowpass).connect(vocalGain).connect(midRecombine);
  midBus.connect(midAir).connect(midRecombine);

  const punch = context.createBiquadFilter();
  punch.type = "peaking";
  punch.frequency.value = 120;
  punch.Q.value = 0.8;
  punch.gain.value = settings.punch * 4;
  const clarity = context.createBiquadFilter();
  clarity.type = "highshelf";
  clarity.frequency.value = 4200;
  clarity.gain.value = settings.clarity * 4;
  const width = context.createGain();
  width.gain.value = 1 + settings.width * 0.8;
  sideBus.connect(punch).connect(clarity).connect(width);

  const merger = context.createChannelMerger(2);
  const sideToLeft = context.createGain();
  const sideToRight = context.createGain();
  sideToRight.gain.value = -1;
  midRecombine.connect(merger, 0, 0);
  midRecombine.connect(merger, 0, 1);
  width.connect(sideToLeft).connect(merger, 0, 0);
  width.connect(sideToRight).connect(merger, 0, 1);

  const makeup = context.createGain();
  makeup.gain.value = 1 + settings.vocalReduction * 0.12;
  const limiter = context.createDynamicsCompressor();
  limiter.threshold.value = -1;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.002;
  limiter.release.value = 0.18;
  merger.connect(makeup).connect(limiter).connect(context.destination);

  source.start();
  return encodeWav(await context.startRendering());
}

export default function InstrumentalEnhancer() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [outputUrl, setOutputUrl] = useState("");
  const [outputName, setOutputName] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState("");
  const [vocalReduction, setVocalReduction] = useState(0.72);
  const [width, setWidth] = useState(0.5);
  const [punch, setPunch] = useState(0.4);
  const [clarity, setClarity] = useState(0.35);
  const busy = phase === "decoding" || phase === "rendering";

  useEffect(() => {
    return () => {
      if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    };
  }, [sourceUrl]);

  useEffect(() => {
    return () => {
      if (outputUrl) URL.revokeObjectURL(outputUrl);
    };
  }, [outputUrl]);

  function chooseFile(nextFile?: File | null) {
    if (!nextFile || busy) return;
    if (!nextFile.type.startsWith("audio/")) {
      setError("Choose an MP3, WAV, M4A, OGG, or FLAC audio file.");
      setPhase("error");
      return;
    }
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    if (outputUrl) URL.revokeObjectURL(outputUrl);
    setFile(nextFile);
    setSourceUrl(URL.createObjectURL(nextFile));
    setOutputUrl("");
    setError("");
    setPhase("ready");
  }

  async function processFile() {
    if (!file || busy) return;
    setError("");
    if (outputUrl) URL.revokeObjectURL(outputUrl);
    setOutputUrl("");
    try {
      const blob = await enhanceInstrumental(
        file,
        { vocalReduction, width, punch, clarity },
        setPhase
      );
      const baseName = file.name.replace(/\.[^.]+$/, "") || "track";
      setOutputName(`${baseName}-instrumental-focus.wav`);
      setOutputUrl(URL.createObjectURL(blob));
      setPhase("done");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "This file could not be processed. Try another audio format."
      );
      setPhase("error");
    }
  }

  const slider = (
    label: string,
    helper: string,
    value: number,
    setter: (value: number) => void
  ) => (
    <label className="block rounded-xl bg-black/20 p-4">
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-bold">{label}</span>
        <span className="text-xs font-black tabular-nums text-[#2EC4B6]">
          {Math.round(value * 100)}%
        </span>
      </span>
      <span className="mt-1 block text-[11px] leading-relaxed text-white/35">{helper}</span>
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={value}
        disabled={busy}
        onChange={(event) => setter(Number(event.target.value))}
        className="mt-4 w-full accent-[#2EC4B6]"
      />
    </label>
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0E0E18] shadow-[0_24px_80px_rgba(0,0,0,0.38)]">
      <div className="grid border-b border-white/[0.07] lg:grid-cols-[1.05fr_0.95fr]">
        <div className="p-5 sm:p-7">
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              chooseFile(event.dataTransfer.files[0]);
            }}
            className="group flex min-h-56 w-full flex-col items-center justify-center rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-6 text-center transition hover:border-[#2EC4B6]/60 hover:bg-[#2EC4B6]/[0.035] disabled:opacity-60"
          >
            <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#2EC4B6]/10 text-[#2EC4B6] transition-transform group-hover:scale-105">
              <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden>
                <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="max-w-full truncate text-base font-bold">
              {file?.name || "Drop a song here"}
            </span>
            <span className="mt-2 text-xs text-white/40">
              or click to choose · up to {MAX_MINUTES} minutes
            </span>
            <span className="mt-5 rounded-full bg-white/[0.05] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[1.4px] text-white/45">
              Never uploaded
            </span>
          </button>
          <input ref={inputRef} type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac" className="hidden" onChange={(event) => chooseFile(event.target.files?.[0])} />
        </div>

        <div className="border-t border-white/[0.07] bg-white/[0.018] p-5 sm:p-7 lg:border-l lg:border-t-0">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[1.8px] text-white/35">Instrumental focus</div>
              <div className="mt-1 text-lg font-black">Shape the mix</div>
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setVocalReduction(0.72);
                setWidth(0.5);
                setPunch(0.4);
                setClarity(0.35);
              }}
              className="text-[10px] font-bold uppercase tracking-[1.4px] text-white/35 transition hover:text-white"
            >
              Reset
            </button>
          </div>
          <div className="grid gap-2">
            {slider("Vocal reduction", "Turns down vocal-heavy center frequencies.", vocalReduction, setVocalReduction)}
            {slider("Stereo width", "Brings out instruments living at the sides.", width, setWidth)}
            {slider("Punch", "Adds weight around kicks and bass instruments.", punch, setPunch)}
            {slider("Clarity", "Lifts percussion, strings, and upper detail.", clarity, setClarity)}
          </div>
        </div>
      </div>

      <div className="p-5 sm:p-7">
        {error && <p role="alert" className="mb-4 text-sm text-[#f3727f]">{error}</p>}
        <button
          type="button"
          disabled={!file || busy}
          onClick={processFile}
          className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-[#2EC4B6] to-[#7CE8DE] px-6 py-4 text-xs font-black uppercase tracking-[1.7px] text-[#06110f] shadow-[0_12px_36px_rgba(46,196,182,0.18)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-35"
        >
          {phase === "decoding" && "Reading your song…"}
          {phase === "rendering" && "Enhancing the instrumental…"}
          {!busy && (outputUrl ? "Render with new settings" : "Enhance instrumental")}
        </button>

        {outputUrl && (
          <div className="mt-7">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[1.8px] text-[#2EC4B6]">Ready to compare</div>
                <h2 className="mt-1 text-lg font-black">Original vs. enhanced</h2>
              </div>
              <a href={outputUrl} download={outputName} title="Download enhanced instrumental as WAV" className="rounded-full border border-[#2EC4B6]/35 px-4 py-2 text-[10px] font-black uppercase tracking-[1.4px] text-[#2EC4B6] transition hover:bg-[#2EC4B6]/10">
                Download WAV
              </a>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl bg-black/25 p-4">
                <div className="mb-3 text-[10px] font-bold uppercase tracking-[1.4px] text-white/35">Original</div>
                <audio controls src={sourceUrl} className="w-full" preload="metadata" />
              </div>
              <div className="rounded-xl border border-[#2EC4B6]/20 bg-[#2EC4B6]/[0.045] p-4">
                <div className="mb-3 text-[10px] font-bold uppercase tracking-[1.4px] text-[#2EC4B6]">Instrumental focus</div>
                <audio controls src={outputUrl} className="w-full" preload="metadata" />
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
